import { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { useNavigate } from 'react-router-dom';

import { exists } from 'lib/types';
import AuthActions, {
  selectActiveOrganization,
  selectAvailableOrganizations
} from 'redux/auth';
import LoadingState from 'components/LoadingState';
import { Modal } from 'lib/components/Modal';
import { Alert } from 'lib/components/Alert';

import { useFirestoreDocumentListener } from 'lib/frontend/hooks/useFirestoreDocumentListener';
import { safeStringify } from 'lib/utils/stringify';
import { uuidv4 } from 'lib/utils/uuid';
import { removeUndefinedFields } from 'lib/helpers';
import { getDynamicRoute, useAppParams } from 'lib/frontend/utils/router';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { MAIN_ROUTES, NOTICE_ROUTES } from 'router/routes';
import {
  CONFIRM_AD,
  getNoticePlacementUrl
} from 'routes/placeScroll/helpers/calculatePlacementSteps';
import { logWarn } from 'utils/logger';
import {
  getActiveDiscountConfigForNotice,
  getUpdatedDiscountLineItemsForNotice,
  wereDiscountLineItemsChanged
} from 'lib/notice/discounts';
import InvoiceForm from './InvoiceForm';
import { getNoticeContentRevisionMillis } from './helpers';
import { getFirebaseContext } from '../../../../utils/firebase';

export default function CreateInvoice() {
  const navigate = useNavigate();
  const { id } = useAppParams(NOTICE_ROUTES.INVOICE_CREATE);
  const dispatch = useAppDispatch();
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const availableOrganizations = useAppSelector(selectAvailableOrganizations);

  const idempotencyKeyRef = useRef(uuidv4());

  const [lastModifiedDate, setLastModifiedDate] = useState<number>();
  const [needRefresh, setNeedRefresh] = useState(false);
  const [openPostModal, setOpenPostModal] = useState<boolean>();

  const noticeSnap = useFirestoreDocumentListener(
    id ? getFirebaseContext().userNoticesRef().doc(id) : undefined
  );

  const {
    value: noticeData,
    isError: isErrorNoticeData,
    invalidateData
  } = useAsyncEffect({
    fetchData: async () => {
      if (!exists(noticeSnap)) {
        return;
      }

      const [rateSnap, advertiserSnap, advertiserOrganization, newspaperSnap] =
        await Promise.all([
          noticeSnap.data().rate.get(),
          noticeSnap.data().filer.get(),
          noticeSnap.data().filedBy?.get(),
          noticeSnap.data().newspaper.get()
        ]);

      if (
        !exists(rateSnap) ||
        !exists(advertiserSnap) ||
        !exists(newspaperSnap) ||
        (advertiserOrganization && !exists(advertiserOrganization))
      ) {
        throw new Error('Missing required data for invoice creation.');
      }

      return {
        rateSnap,
        advertiserSnap,
        advertiserOrganization,
        newspaperSnap
      };
    },
    dependencies: [
      noticeSnap?.data().rate?.id ?? null,
      noticeSnap?.data().filer?.id ?? null,
      noticeSnap?.data().filedBy?.id ?? null,
      noticeSnap?.data().newspaper?.id ?? null,
      getNoticeContentRevisionMillis(noticeSnap)
    ],
    errorConfig: {
      service: ColumnService.PAYMENTS,
      message: 'Error loading notice data for invoice creation',
      tags: {
        noticeId: noticeSnap?.id ?? ''
      }
    }
  });

  const updateDiscountLineItems = async () => {
    if (!exists(noticeSnap)) {
      return;
    }
    const { pricing } = noticeSnap.data();
    if (!pricing) {
      logWarn(
        'Returning early in updateDiscountLineItems due to no pricing on notice.  This is expected for automated placements.',
        { noticeId: noticeSnap.id }
      );
      return;
    }

    const ctx = getFirebaseContext();
    const discountConfig = await getActiveDiscountConfigForNotice(
      ctx,
      noticeSnap.data()
    );

    const updatedLineItems = getUpdatedDiscountLineItemsForNotice(
      discountConfig,
      pricing.lineItems
    );

    const changed = wereDiscountLineItemsChanged(
      pricing.lineItems,
      updatedLineItems
    );
    if (changed) {
      await noticeSnap.ref.update(
        removeUndefinedFields({
          pricing: {
            ...pricing,
            lineItems: updatedLineItems
          }
        })
      );
    }
  };

  useEffect(() => {
    void updateDiscountLineItems();
  }, [safeStringify(noticeSnap?.data()?.pricing)]);

  useEffect(() => {
    if (!exists(noticeSnap)) {
      return;
    }

    if (noticeSnap.data().postWithoutFormatting) {
      setOpenPostModal(true);
    }

    const newLastModifiedDate = getNoticeContentRevisionMillis(noticeSnap);

    if (!lastModifiedDate || newLastModifiedDate === lastModifiedDate) {
      setLastModifiedDate(newLastModifiedDate ?? undefined);
    } else {
      setNeedRefresh(true);
    }
  }, [
    getNoticeContentRevisionMillis(noticeSnap),
    noticeSnap?.data().postWithoutFormatting ?? null
  ]);

  useEffect(() => {
    if (
      noticeData?.newspaperSnap &&
      exists(activeOrganization) &&
      noticeData.newspaperSnap.id !== activeOrganization.id
    ) {
      dispatch(
        AuthActions.setActiveOrganization(
          availableOrganizations.find(o => o.id === noticeData.newspaperSnap.id)
        )
      );
    }
  }, [noticeData?.newspaperSnap?.id, activeOrganization?.id]);

  const handleRefreshInvoice = () => {
    setLastModifiedDate(
      getNoticeContentRevisionMillis(noticeSnap) ?? undefined
    );
    setNeedRefresh(false);
    invalidateData();
  };

  if (isErrorNoticeData) {
    return (
      <Alert
        status="error"
        title="Error loading notice data for invoice creation. Please contact support."
        id="error-loading-notice-data"
      />
    );
  }

  if (
    !exists(noticeSnap) ||
    !noticeData ||
    !exists(noticeData?.newspaperSnap) ||
    noticeData.newspaperSnap?.id !== activeOrganization?.id
  ) {
    return (
      <LoadingState
        context={{
          location: 'CreateInvoice',
          tags: {
            noticeId: noticeSnap?.id ?? ''
          }
        }}
      />
    );
  }

  return (
    <>
      <InvoiceForm
        forward={() => {
          if (!window.location.pathname.includes(MAIN_ROUTES.NOTICES)) {
            navigate(getDynamicRoute(NOTICE_ROUTES.DETAIL, { id }));
          }
        }}
        back={() => {
          navigate(getDynamicRoute(NOTICE_ROUTES.DETAIL, { id }));
        }}
        rateSnap={noticeData.rateSnap}
        advertiserSnap={noticeData.advertiserSnap}
        advertiserOrganization={noticeData.advertiserOrganization || null}
        noticeSnap={noticeSnap}
        newspaperSnap={noticeData.newspaperSnap}
        idempotencyKey={idempotencyKeyRef.current}
        forwardLabel="Review &amp; Submit"
      />
      {needRefresh && (
        <Modal
          allowCloseOutsideModal={false}
          showCloseButton={false}
          id={'refresh-invoice'}
          title="Notice Update"
          primaryAction={{
            type: 'button',
            buttonText: 'Refresh Invoice',
            onClick: handleRefreshInvoice
          }}
          onClose={handleRefreshInvoice}
        >
          <p>
            The notice content has been updated by another user. In order to
            submit your invoice, you must refresh this page to see an updated
            price.
          </p>
        </Modal>
      )}
      {openPostModal && noticeData?.newspaperSnap && (
        <Modal
          id="upload-proof"
          title="Upload Proof"
          onClose={() => setOpenPostModal(false)}
          primaryAction={{
            type: 'button',
            buttonText: 'Continue',
            onClick: () =>
              navigate(getNoticePlacementUrl(id, { step: CONFIRM_AD }))
          }}
        >
          <p>
            Before creating an invoice, please upload a final proof for this
            notice as it will appear in {noticeData.newspaperSnap.data().name}.
          </p>
        </Modal>
      )}
    </>
  );
}
